import type { SecondaryNavigationItemSource } from '~/types/MinifiedSecondaryNavigation';
import type {
  NavigationItemInterface,
  HyperlinkInterface
} from '@hypercodestudio/basler-components';

import { useCreateSecondaryNavItem } from '~/composables/useCreateSecondaryNavItem';

export function useAccountNavigation() {
  const isUriActive = (uri: string, currentPath: string) => {
    return uri === currentPath;
  };

  return {
    getNavigationItems: function (
      navigationItems: SecondaryNavigationItemSource[] | undefined,
      currentPath = useRoute().path
    ) {
      if (!navigationItems || navigationItems.length === 0) {
        return [];
      }

      const result: Array<{
        isActive: boolean;
        text: string;
        link?: HyperlinkInterface;
        items?: NavigationItemInterface[];
      }> = [];

      let currentGroup: {
        isActive: boolean;
        text: string;
        link?: HyperlinkInterface;
        items?: NavigationItemInterface[];
      } | null = null;

      navigationItems.forEach((item: SecondaryNavigationItemSource) => {
        const convertedItem = useCreateSecondaryNavItem(item);

        // Skip items that don't convert properly
        if (!convertedItem) {
          return;
        }
        convertedItem.isActive = isUriActive(
          String(convertedItem?.link?.uri ?? ''),
          currentPath
        );
        const moreLinkText = item.fields?.moreLinkText || '';

        // If we encounter a moreLinkText, it indicates a new group
        if (moreLinkText) {
          // Save the previous group if it exists
          if (currentGroup) {
            result.push(currentGroup);
          }

          // Start a new group
          currentGroup = {
            text: moreLinkText,
            isActive: isUriActive(
              String(convertedItem?.link?.uri ?? ''),
              currentPath
            ),
            items: [convertedItem]
          };
        } else {
          // If no moreLinkText, add to current group or create first group with empty headline
          if (currentGroup) {
            if (!currentGroup.items) {
              currentGroup.items = [];
            }
            currentGroup.items.push(convertedItem);
          } else {
            // First item without moreLinkText - create group with empty headline
            currentGroup = {
              text: convertedItem.text,
              isActive: isUriActive(
                String(convertedItem?.link?.uri ?? ''),
                currentPath
              ),
              link: convertedItem.link
            };
          }
        }
      });

      // Don't forget to add the last group
      if (currentGroup) {
        result.push(currentGroup);
      }

      return result;
    }
  };
}
